import { memo } from 'react';
import highlightParse from 'autosuggest-highlight/parse';
import highlightMatch from 'autosuggest-highlight/match';
import { VStack, Text } from '@library';
import type { SearchResultProduct } from '../../../../queries/useProductSearch';
import ClickableArea from '../../../../../core/components/button/ClickableArea';
import FadeScroll from '../../../../../core/components/layout/FadeScroll/FadeScroll';
import styles from './SkuProductSearchResult.module.scss';

const HighlighText = memo(
    ({ searchTerm, text }: { searchTerm: string; text: string }) => {
        const highlightedParts = highlightParse(
            text,
            highlightMatch(text, searchTerm)
        );
        const index = text.indexOf(searchTerm);

        if (index !== -1) {
            return (
                <>
                    {text.substring(0, index)}
                    <b>{text.substring(index, index + searchTerm.length)}</b>
                    {text.substring(index + searchTerm.length)}
                </>
            );
        }
        return (
            <span>
                {highlightedParts.map(({ text: value, highlight }, idx) => {
                    if (highlight) return <b key={idx + value}>{value}</b>;
                    return value;
                })}
            </span>
        );
    }
);

const SkuProductSearchResult = ({
    products,
    searchTerm,
    openProduct,
}: {
    products: SearchResultProduct[];
    searchTerm: string;
    openProduct: (product: SearchResultProduct) => void;
}) => {
    return (
        <FadeScroll mode="vertical" className={styles.container} largeFade>
            {products.map(product => {
                return (
                    <ClickableArea
                        className={styles.row}
                        key={`${product.id}${product.sku}`}
                        onClick={() => {
                            openProduct(product);
                        }}
                    >
                        <VStack>
                            <Text truncate title={product.sku}>
                                {product.sku && (
                                    <HighlighText
                                        text={product.sku}
                                        searchTerm={searchTerm}
                                    />
                                )}
                            </Text>
                            <Text secondary truncate title={product.name}>
                                <HighlighText
                                    text={product.name}
                                    searchTerm={searchTerm}
                                />
                            </Text>
                        </VStack>
                    </ClickableArea>
                );
            })}
        </FadeScroll>
    );
};

export default memo(SkuProductSearchResult);
